import React from 'react';
import { Avatar, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { AccountCircle, AdminPanelSettings, Logout } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { navMenuItemSx, useNavMenu } from './navMenuStyles';
import { useIsAdmin } from './useIsAdmin';

/** First letter of the user's name or email (for the avatar when there's no picture). */
export const userInitial = (user?: { name?: string; nickname?: string; email?: string }): string =>
  (user?.name || user?.nickname || user?.email || '?').trim().charAt(0).toUpperCase() || '?';

/** The signed-in user's avatar; falls back to their initial if there's no picture or it fails to load. */
export const UserAvatar: React.FC<{ size?: number }> = ({ size = 32 }) => {
  const { user } = useAuth0();
  return (
    <Avatar
      src={user?.picture}
      alt={user?.name || user?.email || 'User'}
      sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: 'primary.main', color: 'primary.contrastText' }}
      imgProps={{ referrerPolicy: 'no-referrer' }}
    >
      {userInitial(user)}
    </Avatar>
  );
};

/** Avatar button in the top bar with a Profile / Log out menu. */
const UserMenu: React.FC = () => {
  const { user, logout } = useAuth0();
  const navigate = useNavigate();
  const menu = useNavMenu('right');
  const close = menu.close;
  const isAdmin = useIsAdmin();

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={menu.open}
          size="small"
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={menu.isOpen}
          sx={{ p: 0.25 }}
        >
          <UserAvatar />
        </IconButton>
      </Tooltip>
      <Menu {...menu.menuProps}>
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <UserAvatar size={36} />
          <Box sx={{ minWidth: 0 }}>
            {user?.name && user.name !== user.email && (
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {user.name}
              </Typography>
            )}
            {user?.email && (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {user.email}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          sx={navMenuItemSx}
          onClick={() => {
            close();
            navigate('/profile');
          }}
        >
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        {isAdmin && (
          <MenuItem
            sx={navMenuItemSx}
            onClick={() => {
              close();
              navigate('/admin');
            }}
          >
            <ListItemIcon>
              <AdminPanelSettings fontSize="small" />
            </ListItemIcon>
            Admin
          </MenuItem>
        )}
        <MenuItem
          sx={navMenuItemSx}
          onClick={() => {
            close();
            logout({ logoutParams: { returnTo: window.location.origin } });
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Log out
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
